import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogHeader,
    DialogBody,
    DialogFooter,
    DialogClose,
} from "@/components/podkit/dialog/Dialog";
import { TrackLocations } from "@/hooks/use-segment";
import { useId, useMemo, useCallback, type FC } from "react";
import { atom, useAtomValue, useSetAtom, type PrimitiveAtom } from "jotai";
import { WorkflowEditForm } from "@/components/automations/AutomationEditForm";
import { useUpdateWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import {
    convertFormStepsToWorkflowStep,
    convertFormTriggersToProtobuf,
    type WorkflowEditFormData,
} from "@/components/automations/automation-edit-form-data";
import { DEFAULT_WORKFLOW_LIMITS, workflowName } from "@/utils/automations";
import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { WorkflowAction, WorkflowAction_Limits } from "gitpod-next-api/gitpod/v1/workflow_pb";

type AutomationEditDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;

    /**
     * Optional submission callback that can be used to handle the form submission.
     * Overrides the default form submission behavior.
     * If not provided, the workflow will be updated with the backend.
     */
    onSubmit?: (values: WorkflowEditFormData) => void;

    workflow?: PlainWorkflow;
};

const AutomationEditDialog: FC<AutomationEditDialogProps> = ({ open, workflow, onOpenChange, onSubmit }) => {
    const formId = useId();
    const isFormDisabledAtom = useMemo(() => atom(true), []);
    const isUpdatingAtom = useMemo(() => atom(false), []);

    const setIsFormDisabled = useSetAtom(isFormDisabledAtom);
    const setIsUpdating = useSetAtom(isUpdatingAtom);

    const { mutateAsync: updateWorkflow } = useUpdateWorkflow();
    const { toast } = useToast();

    const onEditFormSubmit = useCallback(
        async (formData: WorkflowEditFormData) => {
            if (!workflow) return;

            if (onSubmit) {
                onSubmit(formData);
                return;
            }

            const { executor, steps, triggers, limits } = formData;

            setIsUpdating(true);
            try {
                await updateWorkflow({
                    workflowId: workflow.id,
                    executor: executor,
                    action: new WorkflowAction({
                        limits: new WorkflowAction_Limits({
                            maxParallel: limits?.maxParallel ?? DEFAULT_WORKFLOW_LIMITS.maxParallel,
                            maxTotal: limits?.maxTotal ?? DEFAULT_WORKFLOW_LIMITS.maxTotal,
                        }),
                        steps: convertFormStepsToWorkflowStep(steps),
                    }),
                    triggers: triggers ? convertFormTriggersToProtobuf(triggers) : undefined,
                });
                onOpenChange(false);
            } catch (error) {
                toast({
                    title: "Failed to update automation",
                    description: formatError(error),
                });
            } finally {
                setIsUpdating(false);
            }
        },
        [workflow, updateWorkflow, onOpenChange, toast, onSubmit, setIsUpdating],
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-screen-md" data-track-location={TrackLocations.AutomationEditDialog}>
                <DialogHeader>
                    <DialogTitle>Edit Automation {`'${workflowName(workflow)}'`}</DialogTitle>
                </DialogHeader>
                <DialogBody className="p-0">
                    {workflow ? (
                        <WorkflowEditForm
                            workflow={workflow}
                            formName={formId}
                            onDisabledChange={setIsFormDisabled}
                            onSubmit={onEditFormSubmit}
                        />
                    ) : null}
                </DialogBody>
                <AutomationEditDialogFooter
                    formId={formId}
                    isFormDisabledAtom={isFormDisabledAtom}
                    isUpdatingAtom={isUpdatingAtom}
                />
            </DialogContent>
        </Dialog>
    );
};

type AutomationEditDialogFooterProps = {
    formId: string;
    isFormDisabledAtom: PrimitiveAtom<boolean>;
    isUpdatingAtom: PrimitiveAtom<boolean>;
};

const AutomationEditDialogFooter: FC<AutomationEditDialogFooterProps> = ({
    formId,
    isFormDisabledAtom,
    isUpdatingAtom,
}) => {
    const isFormDisabled = useAtomValue(isFormDisabledAtom);
    const isUpdating = useAtomValue(isUpdatingAtom);

    return (
        <DialogFooter className="justify-end">
            <DialogClose asChild>
                <Button variant="outline" disabled={isUpdating}>
                    Cancel
                </Button>
            </DialogClose>
            <Button
                form={formId}
                variant="primary"
                type="submit"
                data-tracking-id="save-changes-automation-edit-dialog"
                loading={isUpdating}
                disabled={isFormDisabled}
            >
                Save
            </Button>
        </DialogFooter>
    );
};

export default AutomationEditDialog;
